import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import SlidingPane from "react-sliding-pane";
import Flatpickr from "react-flatpickr";
import Select from 'react-select';
import Tippy from '@tippyjs/react';
import { toast } from 'react-toastify';

import {
  offsetDateForBrowser,
  toCalendarDate
} from '../../../shared/timeZoneLogic';

const axios = require('axios').default;

const PAYMENT_METHODS = [
  { value: "Credit Card", label: "Credit Card" },
  { value: "Checking Account", label: "Checking Account" },
  { value: "ACH", label: "ACH" },
  { value: "Cash", label: "Cash" },
  { value: "Other", label: "Other" }
];

const getCategoryLabel = (category) => {
  const labels = {
    "TICKET": "Ticket",
    "ADDITION": "Addition",
    "INCOME": "Income",
    "EXPENSES": "Expense",
    "BEFORE_ADJUSTED_DEDUCTIONS": "Before Adjusted Deduction",
    "DEDUCTIONS": "Deduction"
  };
  return labels[category] || labels[category?.toUpperCase()] || category || "—";
};

const getCalcTypeLabel = (calcType) => {
  const labels = {
    "fixed": "Fixed",
    "per_tickets_sold": "Per Ticket Sold",
    "percentage_of_gross": "% of Gross",
    "percentage_of_net_gross": "% of Net Gross (Multiplier)",
    "tax_inclusive_percentage_of_net_gross": "% of Net Gross (Divisor)",
    "percentage_of_net": "% of Net",
    "percentage_of_expenses": "% of Expenses",
    "tax_inclusive_percentage": "% of Gross (Tax-Inclusive)"
  };
  return labels[calcType] || calcType;
};

const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(num);
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: "var(--sl-border-primary)",
    borderRadius: "8px",
    minHeight: "42px",
    backgroundColor: state.isDisabled
      ? "var(--sl-bg-secondary)"
      : "var(--sl-bg-primary)"
  }),
  singleValue: (base) => ({ ...base, color: "var(--sl-text-primary)" }),
  input: (base) => ({ ...base, color: "var(--sl-text-primary)" }),
  placeholder: (base) => ({ ...base, color: "var(--sl-text-placeholder)" }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--sl-bg-primary)",
    border: "1px solid var(--sl-border-secondary)"
  }),
  option: (base, state) => ({
    ...base,
    color: "var(--sl-text-primary)",
    backgroundColor: state.isFocused
      ? "var(--sl-bg-secondary)"
      : "var(--sl-bg-primary)"
  })
};

const BudgetLineItemDetailsPane = ({
  isOpen,
  onClose,
  budgetLineItem,
  csrfToken,
  team,
  offer,
  budget,
  updateBudget,
  updatedBudgetWithBudgetLineItem,
  performers = [],
  financeCategories = []
}) => {
  const [formData, setFormData] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (budgetLineItem && Object.keys(budgetLineItem).length > 0) {
      setFormData({
        due_on: budgetLineItem.due_on
          ? offsetDateForBrowser(budgetLineItem.due_on)
          : null,
        payment_method: budgetLineItem.payment_method || "",
        payee: budgetLineItem.payee || "",
        paid: budgetLineItem.paid || false,
        performer_id: budgetLineItem.performer_id || null,
        on_settlement: budgetLineItem.on_settlement || false,
        finance_category_id: budgetLineItem.finance_category_id || null,
        finance_category: budgetLineItem.finance_category || null,
        show_in_offer: budgetLineItem.show_in_offer !== false,
        show_on_forecast: budgetLineItem.show_on_forecast !== false,
        deducted_before_split: budgetLineItem.deducted_before_split || false
      });
      setIsDirty(false);
    }
  }, [budgetLineItem]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    try {
      const params = new FormData();

      params.append('v3_budget_line_item[due_on]', formData.due_on ? toCalendarDate(formData.due_on) : '');
      params.append('v3_budget_line_item[payment_method]', formData.payment_method || '');
      params.append('v3_budget_line_item[payee]', formData.payee || '');
      params.append('v3_budget_line_item[paid]', formData.paid || false);
      params.append('v3_budget_line_item[performer_id]', formData.performer_id || '');
      params.append('v3_budget_line_item[finance_category_id]', formData.finance_category_id || '');
      if (showDeductedBeforeSplit) {
        params.append('v3_budget_line_item[deducted_before_split]', formData.deducted_before_split || false);
      }

      const response = await axios.patch(
        `/teams/${team.id}/budget_line_items/v3/${budgetLineItem.id}`,
        params,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const updatedLineItem = response.data;
      const updatedBudget = updatedBudgetWithBudgetLineItem(budget, updatedLineItem);
      updateBudget(updatedBudget);

      toast.success('Details saved successfully', {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: true,
        autoClose: 3000,
        hideProgressBar: true
      });

      setIsDirty(false);
      onClose();
    } catch (error) {
      const serverErrors = error.response?.data;
      const message = Array.isArray(serverErrors) ? serverErrors.join(', ') : 'Failed to save details';
      toast.error(message, {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({});
    setIsDirty(false);
    onClose();
  };

  const performerOptions = (performers || []).map(p => ({
    value: p.id,
    label: `${p.artistable?.name || 'Unknown'} - ${p.humanized_act_type || 'Artist'}`
  }));

  const selectedPerformer = performerOptions.find(
    p => p.value === formData.performer_id
  );

  const selectedPaymentMethod = PAYMENT_METHODS.find(
    m => m.value === formData.payment_method
  );

  const financeCategoryName =
    financeCategories.find(c => c.id === budgetLineItem.finance_category_id)?.name ||
    budgetLineItem.finance_category?.name ||
    "—";

  const showDeductedBeforeSplit =
    budgetLineItem.category === 'EXPENSES' && offer?.revenue_base === 'adjusted_gross';


  if (!budgetLineItem || Object.keys(budgetLineItem).length === 0) {
    return null;
  }

  return (
    <SlidingPane
      isOpen={isOpen}
      className="budget-line-item-details-pane"
      hideHeader={true}
      width="658px"
      onRequestClose={handleCancel}
      overlayClassName="budget-line-item-details-pane-overlay"
    >
      <style>{`
        .budget-line-item-details-pane-overlay {
          z-index: 100 !important;
        }
        .budget-line-item-details-pane.slide-pane {
          border-radius: 8px;
          margin-top: 8px;
          margin-bottom: 8px;
          margin-right: 8px;
          right: 0;
          left: auto !important;
          height: calc(100% - 16px) !important;
          background: var(--sl-bg-primary);
          box-shadow: 0px 12px 16px -4px rgba(10, 13, 18, 0.08),
                      0px 4px 6px -2px rgba(10, 13, 18, 0.03),
                      0px 2px 2px -1px rgba(10, 13, 18, 0.04);
        }
        .budget-line-item-details-pane .slide-pane__content {
          border-radius: 8px;
          overflow: hidden;
          padding: 0;
        }
      `}</style>
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "var(--sl-bg-primary)",
        borderRadius: "8px"
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--sl-border-secondary)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <h5 style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: 700,
            color: "var(--sl-text-primary)"
          }}>
            Edit Details
          </h5>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              background: "none",
              border: "none",
              padding: "8px",
              cursor: "pointer",
              borderRadius: "8px"
            }}
          >
            <i className="fas fa-times" style={{ color: "var(--sl-fg-tertiary)" }}></i>
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px"
        }}>
          {/* Item Summary Card */}
          <div style={{
            backgroundColor: "var(--sl-bg-secondary)",
            borderRadius: "8px",
            padding: "8px 16px 16px 16px",
            marginBottom: "24px"
          }}>
            <div style={{ marginBottom: "10px" }}>
              <span style={{
                display: "inline-block",
                backgroundColor: "var(--sl-bg-brand-primary)",
                border: "1px solid var(--sl-border-brand)",
                borderRadius: "9999px",
                padding: "2px 8px",
                fontSize: "12px",
                color: "var(--sl-text-brand)",
                fontWeight: 500
              }}>
                {getCategoryLabel(budgetLineItem.category)}
              </span>
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "8px 12px"
            }}>
              <div>
                <p style={{
                  fontSize: "12px",
                  color: "var(--sl-text-secondary)",
                  margin: "0 0 2px 0",
                  lineHeight: "18px"
                }}>Item</p>
                <p style={{
                  fontSize: "14px",
                  color: "var(--sl-text-primary)",
                  margin: 0,
                  lineHeight: "20px"
                }}>{budgetLineItem.name}</p>
              </div>
              <div>
                <p style={{
                  fontSize: "12px",
                  color: "var(--sl-text-secondary)",
                  margin: "0 0 2px 0",
                  lineHeight: "18px"
                }}>Type</p>
                <p style={{
                  fontSize: "14px",
                  color: "var(--sl-text-primary)",
                  margin: 0,
                  lineHeight: "20px"
                }}>{getCalcTypeLabel(budgetLineItem.calc_type)}</p>
              </div>
              <div>
                <p style={{
                  fontSize: "12px",
                  color: "var(--sl-text-secondary)",
                  margin: "0 0 2px 0",
                  lineHeight: "18px"
                }}>Category</p>
                <p style={{
                  fontSize: "14px",
                  color: "var(--sl-text-primary)",
                  margin: 0,
                  lineHeight: "20px"
                }}>{financeCategoryName}</p>
              </div>
              <div>
                <p style={{
                  fontSize: "12px",
                  color: "var(--sl-text-secondary)",
                  margin: "0 0 2px 0",
                  lineHeight: "18px"
                }}>Amount</p>
                <p style={{
                  fontSize: "14px",
                  color: "var(--sl-text-primary)",
                  margin: 0,
                  lineHeight: "20px"
                }}>{formatCurrency(budgetLineItem.left_hand)}</p>
              </div>
              <div>
                <p style={{
                  fontSize: "12px",
                  color: "var(--sl-text-secondary)",
                  margin: "0 0 2px 0",
                  lineHeight: "18px"
                }}>QTY</p>
                <p style={{
                  fontSize: "14px",
                  color: "var(--sl-text-primary)",
                  margin: 0,
                  lineHeight: "20px"
                }}>{budgetLineItem.right_hand}</p>
              </div>
              <div>
                <p style={{
                  fontSize: "12px",
                  color: "var(--sl-text-secondary)",
                  margin: "0 0 2px 0",
                  lineHeight: "18px"
                }}>Total</p>
                <p style={{
                  fontSize: "14px",
                  color: "var(--sl-text-primary)",
                  margin: 0,
                  lineHeight: "20px"
                }}>{formatCurrency(budgetLineItem.calculated_total)}</p>
              </div>
            </div>
          </div>

          {/* Payment Info Section */}
          <h6 style={{
            fontSize: "16px",
            fontWeight: 600,
            color: "var(--sl-text-primary)",
            marginBottom: "24px"
          }}>
            Payment Info
          </h6>

          {/* Payment Date */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--sl-text-secondary)",
              marginBottom: "6px"
            }}>
              Payment Date
            </label>
            <Flatpickr
              value={formData.due_on || null}
              onChange={([date]) => handleFieldChange('due_on', date || null)}
              options={{
                dateFormat: "m/d/Y",
                allowInput: true
              }}
              placeholder="Enter payment date"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid var(--sl-border-primary)",
                borderRadius: "8px",
                fontSize: "16px",
                color: "var(--sl-text-primary)",
                backgroundColor: "var(--sl-bg-primary)"
              }}
            />
          </div>

          {/* Payment Method */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--sl-text-secondary)",
              marginBottom: "6px"
            }}>
              Payment Method
            </label>
            <Select
              value={selectedPaymentMethod}
              onChange={(option) => handleFieldChange('payment_method', option?.value || "")}
              options={PAYMENT_METHODS}
              isClearable
              placeholder="Select payment method"
              styles={selectStyles}
            />
          </div>

          {/* Payee */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--sl-text-secondary)",
              marginBottom: "6px"
            }}>
              Payee
            </label>
            <input
              type="text"
              value={formData.payee || ""}
              onChange={(e) => handleFieldChange('payee', e.target.value)}
              placeholder="Enter payee name"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid var(--sl-border-primary)",
                borderRadius: "8px",
                fontSize: "16px",
                color: "var(--sl-text-primary)",
                backgroundColor: "var(--sl-bg-primary)"
              }}
            />
          </div>

          {/* Mark as Paid Checkbox */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              cursor: "pointer"
            }}>
              <input
                type="checkbox"
                checked={formData.paid || false}
                onChange={(e) => handleFieldChange('paid', e.target.checked)}
                style={{
                  width: "16px",
                  height: "16px",
                  marginTop: "2px",
                  cursor: "pointer"
                }}
              />
              <span style={{
                fontSize: "14px",
                fontWeight: 500,
                color: "var(--sl-text-secondary)"
              }}>
                Mark as paid
              </span>
            </label>
          </div>

          {showDeductedBeforeSplit && (
            <div style={{ marginBottom: "24px" }}>
              <label style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                cursor: "pointer"
              }}>
                <input
                  type="checkbox"
                  checked={!!formData.deducted_before_split}
                  onChange={(e) => handleFieldChange('deducted_before_split', e.target.checked)}
                  style={{
                    width: "16px",
                    height: "16px",
                    marginTop: "2px",
                    cursor: "pointer"
                  }}
                />
                <span style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "var(--sl-text-secondary)"
                }}>
                  Deducted before split
                </span>
              </label>
              <p style={{ fontSize: "12px", color: "var(--sl-text-tertiary)", margin: "4px 0 0 24px" }}>
                Subtracts this expense from the revenue base before the artist&apos;s percentage is applied.
              </p>
            </div>
          )}

          {/* Divider */}
          <hr style={{
            border: "none",
            borderTop: "1px solid var(--sl-border-secondary)",
            margin: "24px 0"
          }} />

          {/* Artist Designation Section */}
          <h6 style={{
            fontSize: "16px",
            fontWeight: 600,
            color: "var(--sl-text-primary)",
            marginBottom: "24px"
          }}>
            Artist Designation
          </h6>

          {/* Assign to Artist */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--sl-text-secondary)",
              marginBottom: "6px"
            }}>
              Assign to artist
              <Tippy
                content="Only this artist's settlement will include the line. Leave it unassigned for a cost the whole show shares."
                placement="top"
                delay={[300, 0]}
              >
                <i
                  className="fas fa-info-circle ml-2"
                  style={{ fontSize: "12px", color: "var(--sl-text-secondary)", cursor: "help" }}
                  aria-hidden="true"
                />
              </Tippy>
            </label>
            <Select
              value={selectedPerformer}
              onChange={(option) => handleFieldChange('performer_id', option?.value || null)}
              options={performerOptions}
              isClearable
              placeholder="Select artist"
              isDisabled={performerOptions.length === 0}
              styles={selectStyles}
            />
          </div>

          {/* Divider */}
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px",
          borderTop: "1px solid var(--sl-border-secondary)",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px"
        }}>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              padding: "10px 14px",
              border: "1px solid var(--sl-border-primary)",
              borderRadius: "8px",
              backgroundColor: "var(--sl-bg-primary)",
              fontSize: "14px",
              fontWeight: 600,
              color: "var(--sl-text-secondary)",
              cursor: "pointer"
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            style={{
              padding: "10px 14px",
              border: "1px solid var(--sl-border-secondary)",
              borderRadius: "8px",
              backgroundColor: isDirty && !isSaving ? "var(--sl-bg-brand-solid-hover)" : "var(--sl-bg-tertiary)",
              fontSize: "14px",
              fontWeight: 600,
              color: isDirty && !isSaving ? "var(--sl-text-white)" : "var(--sl-text-disabled)",
              cursor: isDirty && !isSaving ? "pointer" : "not-allowed"
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </SlidingPane>
  );
};

BudgetLineItemDetailsPane.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  budgetLineItem: PropTypes.object,
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  budget: PropTypes.object.isRequired,
  updateBudget: PropTypes.func.isRequired,
  updatedBudgetWithBudgetLineItem: PropTypes.func.isRequired,
  performers: PropTypes.array,
  financeCategories: PropTypes.array
};

export default BudgetLineItemDetailsPane;

